import React, { useState, useMemo } from 'react';
import { Printer, ChevronDown, ChevronUp } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { calculateGrade, formatDate } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';

function Results() {
  const { exams, marks, students, classes, subjects, parents, staff, feePayments, feeStructures, routes } = useApp();
  const { currentUser } = useAuth();

  const isStudentRole = currentUser?.role === 'student';
  const isParentRole = ['parent','father','mother'].includes(currentUser?.role);
  const isTeacher = currentUser?.role === 'teacher';
  const isRestricted = isStudentRole || isParentRole;
  const teacherProfile = isTeacher ? staff.find(s => s.id === currentUser?.profileId) : null;
  const availableClasses = isTeacher
    ? classes.filter(c => (teacherProfile?.classIds || []).includes(c.id))
    : classes;

  const [selectedExam, setSelectedExam] = useState(exams.find(e => e.status === 'completed')?.id || exams[0]?.id || '');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [view, setView] = useState('class'); // 'class' | 'subject'

  React.useEffect(() => {
    if (isStudentRole && currentUser?.profileId) {
      const s = students.find(st => st.id === currentUser.profileId);
      if (s) setSelectedClass(s.classId);
    }
  }, []);

  const exam = exams.find(e => e.id === selectedExam);

  const gradeColor = (g) => {
    if (!g) return '#6b7280';
    if (g === 'A+') return '#16a34a';
    if (g === 'A')  return '#22c55e';
    if (g === 'B+') return '#2563eb';
    if (g === 'B')  return '#3b82f6';
    if (g === 'C+') return '#d97706';
    if (g === 'C')  return '#f59e0b';
    if (g === 'D')  return '#ea580c';
    return '#dc2626';
  };

  const barColor = (pct) => {
    if (pct >= 80) return '#16a34a';
    if (pct >= 60) return '#2563eb';
    if (pct >= 40) return '#f59e0b';
    return '#dc2626';
  };

  // All marks for this exam
  const examMarks = marks.filter(m => m.examId === selectedExam);

  // Students in selected class (active)
  const classStudents = useMemo(() => {
    let list = students.filter(s => s.status === 'active');
    if (isStudentRole && currentUser?.profileId) return list.filter(s => s.id === currentUser.profileId);
    if (isParentRole && currentUser?.profileId) list = list.filter(s => s.parentId === currentUser.profileId);
    if (isTeacher) list = list.filter(s => availableClasses.some(c => c.id === s.classId));
    if (selectedClass) list = list.filter(s => s.classId === selectedClass);
    return list.sort((a, b) => (a.rollNumber || '').localeCompare(b.rollNumber || '', undefined, { numeric: true }));
  }, [students, selectedClass, isStudentRole, isParentRole, isTeacher, availableClasses, currentUser]);

  // Subjects that have marks entered for this exam + class
  const examSubjects = useMemo(() => {
    const subIds = [...new Set(examMarks
      .filter(m => classStudents.some(s => s.id === m.studentId))
      .map(m => m.subjectId))];
    return subIds.map(id => subjects.find(s => s.id === id)).filter(Boolean);
  }, [examMarks, classStudents, subjects]);

  // Build per-student result rows
  const studentResults = useMemo(() => {
    return classStudents.map(s => {
      const sMarks = examMarks.filter(m => m.studentId === s.id);
      const total = sMarks.reduce((sum, m) => sum + m.marksObtained, 0);
      const maxTotal = sMarks.reduce((sum, m) => sum + m.maxMarks, 0);
      const pct = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
      const grade = maxTotal > 0 ? calculateGrade(total, maxTotal) : null;
      const passed = sMarks.length > 0 && sMarks.every(m => (m.marksObtained / m.maxMarks) >= 0.33);
      const subjectMap = {};
      sMarks.forEach(m => { subjectMap[m.subjectId] = m; });
      const parent = parents?.find(p => p.id === s.parentId);
      return { ...s, sMarks, total, maxTotal, pct, grade, passed, subjectMap, parent };
    }).map((s, _, arr) => {
      const rank = arr.filter(o => o.pct > s.pct).length + 1;
      return { ...s, rank };
    });
  }, [classStudents, examMarks, parents]);

  // Subject-wise analysis
  const subjectResults = useMemo(() => {
    if (!selectedSubject) return [];
    return classStudents.map(s => {
      const m = examMarks.find(m => m.studentId === s.id && m.subjectId === selectedSubject);
      if (!m) return null;
      const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
      const grade = calculateGrade(m.marksObtained, m.maxMarks);
      return { ...s, marksObtained: m.marksObtained, maxMarks: m.maxMarks, pct, grade };
    }).filter(Boolean)
      .sort((a, b) => b.pct - a.pct)
      .map((s, i) => ({ ...s, rank: i + 1 }));
  }, [selectedSubject, classStudents, examMarks]);

  // Print individual report card
  const printReportCard = async (sr) => {
    const school = await getSchoolInfo();
    const cls = classes.find(c => c.id === sr.classId);
    const totalMarks = sr.total;
    const totalMax = sr.maxTotal;
    const overallPct = sr.pct;
    const overallGrade = sr.grade;
    const gradeBg = overallGrade ? gradeColor(overallGrade.grade) : '#6b7280';
    const resultLabel = sr.passed ? 'PASS' : 'FAIL';
    const resultColor = sr.passed ? '#16a34a' : '#dc2626';

    const subjectRows = examSubjects.map(sub => {
      const m = sr.subjectMap[sub.id];
      if (!m) return '';
      const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
      const grade = calculateGrade(m.marksObtained, m.maxMarks);
      return `<tr>
        <td>${sub.name}</td>
        <td class="center">${m.maxMarks}</td>
        <td class="center"><strong>${m.marksObtained}</strong></td>
        <td class="center">${pct}%</td>
        <td class="center"><div class="bar-wrap"><div class="bar" style="width:${pct}%;background:${barColor(pct)}"></div></div></td>
        <td class="center grade" style="color:${gradeColor(grade.grade)}">${grade.grade}</td>
        <td>${m.remarks || '—'}</td>
      </tr>`;
    }).join('');

    const win = window.open('', '_blank', 'width=900,height=800');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Report Card — ${sr.firstName} ${sr.lastName}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; background:#f0f4f8; color:#1e293b; font-size:13px; }
  .page { max-width:860px; margin:24px auto; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.12); }
  .student-strip { display:flex; align-items:center; gap:20px; background:#f8fafc; border-bottom:1px solid #e2e8f0; padding:14px 20px; }
  .avatar { width:48px; height:48px; border-radius:50%; background:linear-gradient(135deg,#6b21a8,#9333ea); color:#fff; display:flex; align-items:center; justify-content:center; font-size:18px; font-weight:700; flex-shrink:0; }
  .s-name { font-size:16px; font-weight:700; color:#0f172a; }
  .s-meta { font-size:12px; color:#64748b; margin-top:2px; }
  .s-details { display:flex; gap:24px; margin-left:auto; }
  .lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; }
  .val { font-size:12px; font-weight:600; color:#1e293b; margin-top:1px; }
  .score-section { display:flex; border-bottom:1px solid #e2e8f0; }
  .score-box { flex:1; padding:12px 16px; text-align:center; border-right:1px solid #e2e8f0; }
  .score-box:last-child { border-right:none; }
  .s-lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:4px; }
  .s-val { font-size:22px; font-weight:900; }
  .s-sub { font-size:11px; color:#94a3b8; margin-top:2px; }
  .grade-circle { display:inline-flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; font-size:18px; font-weight:900; color:#fff; }
  .table-wrap { padding:14px 20px 18px; }
  .section-title { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:1px; margin-bottom:10px; }
  table { width:100%; border-collapse:collapse; }
  thead tr { background:#f1f5f9; }
  thead th { padding:8px 10px; font-size:11px; font-weight:700; color:#475569; text-align:left; border-bottom:2px solid #e2e8f0; }
  thead th.center { text-align:center; }
  tbody tr { border-bottom:1px solid #f1f5f9; }
  td { padding:8px 10px; font-size:12px; color:#334155; }
  td.center { text-align:center; }
  td.grade { font-size:14px; font-weight:800; }
  .bar-wrap { width:60px; height:6px; background:#e2e8f0; border-radius:99px; margin:0 auto; overflow:hidden; }
  .bar { height:100%; border-radius:99px; }
  tfoot tr { background:#f8fafc; border-top:2px solid #e2e8f0; }
  tfoot td { padding:10px; font-weight:700; font-size:13px; color:#0f172a; }
  .card-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 20px; background:#f8fafc; border-top:1px solid #e2e8f0; }
  .sig-line { width:120px; border-top:1px solid #94a3b8; padding-top:4px; font-size:10px; color:#94a3b8; text-align:center; }
  .print-btn { text-align:center; padding:14px; background:#f0f4f8; }
  @media print { .print-btn{display:none;} body{background:#fff;} .page{box-shadow:none;margin:0;border-radius:0;} *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;} img{display:block!important;} }
</style></head><body>
<div class="page">
  <div style="padding:14px 20px 0">
    ${schoolHeader(school)}
    <div style="text-align:center;font-size:14px;font-weight:700;color:#374151;margin:-6px 0 10px;text-transform:uppercase">Report Card &mdash; ${exam?.name || 'Examination'} &mdash; ${exam?.academicYear || '2025-2026'}</div>
  </div>
  <div class="student-strip">
    <div class="avatar">${sr.firstName?.[0]}${sr.lastName?.[0]}</div>
    <div><div class="s-name">${sr.firstName} ${sr.lastName}</div><div class="s-meta">${cls ? `${cls.name}-${cls.section}` : ''} &nbsp;·&nbsp; Roll No: ${sr.rollNumber || '—'}</div></div>
    <div class="s-details">
      <div><div class="lbl">Adm No</div><div class="val">${sr.admissionNumber || '—'}</div></div>
      <div><div class="lbl">Parent</div><div class="val">${sr.parent?.name || '—'}</div></div>
      <div><div class="lbl">Phone</div><div class="val">${sr.parent?.phone || '—'}</div></div>
      <div><div class="lbl">Rank</div><div class="val">${sr.rank}</div></div>
    </div>
  </div>
  <div class="score-section">
    <div class="score-box"><div class="s-lbl">Total Marks</div><div class="s-val" style="color:#6b21a8">${totalMarks} / ${totalMax}</div><div class="s-sub">Subjects: ${examSubjects.length}</div></div>
    <div class="score-box"><div class="s-lbl">Percentage</div><div class="s-val" style="color:${barColor(overallPct)}">${overallPct}%</div><div class="s-sub">${overallPct >= 75 ? 'Distinction' : overallPct >= 60 ? 'First Class' : overallPct >= 50 ? 'Second Class' : overallPct >= 33 ? 'Pass' : 'Fail'}</div></div>
    <div class="score-box"><div class="s-lbl">Overall Grade</div><div style="margin-top:2px"><span class="grade-circle" style="background:${gradeBg}">${overallGrade?.grade || '—'}</span></div><div class="s-sub">Grade Points: ${overallGrade?.gp ?? '—'}</div></div>
    <div class="score-box"><div class="s-lbl">Result</div><div class="s-val" style="color:${resultColor}">${resultLabel}</div><div class="s-sub">${formatDate(new Date().toISOString().split('T')[0])}</div></div>
  </div>
  <div class="table-wrap">
    <div class="section-title">Subject-wise Performance</div>
    <table>
      <thead><tr><th>Subject</th><th class="center">Max</th><th class="center">Obtained</th><th class="center">%</th><th class="center">Progress</th><th class="center">Grade</th><th>Remarks</th></tr></thead>
      <tbody>${subjectRows}</tbody>
      <tfoot><tr>
        <td>Grand Total</td><td class="center">${totalMax}</td><td class="center">${totalMarks}</td><td class="center">${overallPct}%</td>
        <td class="center"><div class="bar-wrap"><div class="bar" style="width:${overallPct}%;background:${barColor(overallPct)}"></div></div></td>
        <td class="center grade" style="color:${gradeBg}">${overallGrade?.grade || '—'}</td><td></td>
      </tr></tfoot>
    </table>
  </div>
  <div class="card-footer">
    <div class="sig-line">Class Teacher</div>
    <div style="font-size:10px;color:#94a3b8;text-align:center">Computer-generated report card<br/>${school.name}</div>
    <div class="sig-line">Principal</div>
  </div>
</div>
<div class="print-btn">
  <button onclick="window.print()" style="background:linear-gradient(135deg,#6b21a8,#9333ea);color:#fff;border:none;padding:12px 36px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Roboto',sans-serif">&#128424;&nbsp; Print Report Card</button>
</div>
</body></html>`); win.document.close();
  };

  // Print all students report cards (one per page)
  const printAllReportCards = async () => {
    const school = await getSchoolInfo();
    const allPages = studentResults.map(sr => {
      const cls = classes.find(c => c.id === sr.classId);
      const totalMarks = sr.total;
      const totalMax = sr.maxTotal;
      const overallPct = sr.pct;
      const overallGrade = sr.grade;
      const gradeBg = overallGrade ? gradeColor(overallGrade.grade) : '#6b7280';
      const resultLabel = sr.passed ? 'PASS' : 'FAIL';
      const resultColor = sr.passed ? '#16a34a' : '#dc2626';

      const subjectRows = examSubjects.map(sub => {
        const m = sr.subjectMap[sub.id];
        if (!m) return '';
        const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
        const grade = calculateGrade(m.marksObtained, m.maxMarks);
        return `<tr>
          <td>${sub.name}</td>
          <td class="center">${m.maxMarks}</td>
          <td class="center"><strong>${m.marksObtained}</strong></td>
          <td class="center">${pct}%</td>
          <td class="center"><div class="bar-wrap"><div class="bar" style="width:${pct}%;background:${barColor(pct)}"></div></div></td>
          <td class="center grade" style="color:${gradeColor(grade.grade)}">${grade.grade}</td>
          <td>${m.remarks || '—'}</td>
        </tr>`;
      }).join('');

      return `
      <div class="page">
        <div style="padding:14px 20px 0">
          ${schoolHeader(school)}
          <div style="text-align:center;font-size:14px;font-weight:700;color:#374151;margin:-6px 0 10px;text-transform:uppercase">Report Card &mdash; ${exam?.name || 'Examination'} &mdash; ${exam?.academicYear || '2025-2026'}</div>
        </div>
        <div class="student-strip">
          <div class="avatar">${sr.firstName?.[0]}${sr.lastName?.[0]}</div>
          <div><div class="s-name">${sr.firstName} ${sr.lastName}</div><div class="s-meta">${cls ? `${cls.name}-${cls.section}` : ''} &nbsp;·&nbsp; Roll No: ${sr.rollNumber || '—'}</div></div>
          <div class="s-details">
            <div><div class="lbl">Adm No</div><div class="val">${sr.admissionNumber || '—'}</div></div>
            <div><div class="lbl">Parent</div><div class="val">${sr.parent?.name || '—'}</div></div>
            <div><div class="lbl">Phone</div><div class="val">${sr.parent?.phone || '—'}</div></div>
            <div><div class="lbl">Rank</div><div class="val">${sr.rank}</div></div>
          </div>
        </div>
        <div class="score-section">
          <div class="score-box"><div class="s-lbl">Total Marks</div><div class="s-val" style="color:#6b21a8">${totalMarks} / ${totalMax}</div><div class="s-sub">Subjects: ${examSubjects.length}</div></div>
          <div class="score-box"><div class="s-lbl">Percentage</div><div class="s-val" style="color:${barColor(overallPct)}">${overallPct}%</div><div class="s-sub">${overallPct >= 75 ? 'Distinction' : overallPct >= 60 ? 'First Class' : overallPct >= 50 ? 'Second Class' : overallPct >= 33 ? 'Pass' : 'Fail'}</div></div>
          <div class="score-box"><div class="s-lbl">Overall Grade</div><div style="margin-top:2px"><span class="grade-circle" style="background:${gradeBg}">${overallGrade?.grade || '—'}</span></div><div class="s-sub">Grade Points: ${overallGrade?.gp ?? '—'}</div></div>
          <div class="score-box"><div class="s-lbl">Result</div><div class="s-val" style="color:${resultColor}">${resultLabel}</div><div class="s-sub">${formatDate(new Date().toISOString().split('T')[0])}</div></div>
        </div>
        <div class="table-wrap">
          <div class="section-title">Subject-wise Performance</div>
          <table>
            <thead><tr><th>Subject</th><th class="center">Max</th><th class="center">Obtained</th><th class="center">%</th><th class="center">Progress</th><th class="center">Grade</th><th>Remarks</th></tr></thead>
            <tbody>${subjectRows}</tbody>
            <tfoot><tr>
              <td>Grand Total</td><td class="center">${totalMax}</td><td class="center">${totalMarks}</td><td class="center">${overallPct}%</td>
              <td class="center"><div class="bar-wrap"><div class="bar" style="width:${overallPct}%;background:${barColor(overallPct)}"></div></div></td>
              <td class="center grade" style="color:${gradeBg}">${overallGrade?.grade || '—'}</td><td></td>
            </tr></tfoot>
          </table>
        </div>
        <div class="card-footer">
          <div class="sig-line">Class Teacher</div>
          <div style="font-size:10px;color:#94a3b8;text-align:center">Computer-generated report card<br/>${school.name}</div>
          <div class="sig-line">Principal</div>
        </div>
      </div>`;
    }).join('');

    const win = window.open('', '_blank', 'width=960,height=800');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Report Cards — ${exam?.name || 'Examination'}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; background:#e5e7eb; color:#1e293b; font-size:13px; }
  .page { max-width:860px; margin:24px auto; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,0.12); page-break-after:always; }
  .page:last-child { page-break-after:auto; }
  .student-strip { display:flex; align-items:center; gap:20px; background:#f8fafc; border-bottom:1px solid #e2e8f0; padding:14px 20px; }
  .avatar { width:48px; height:48px; border-radius:50%; background:linear-gradient(135deg,#6b21a8,#9333ea); color:#fff; display:flex; align-items:center; justify-content:center; font-size:18px; font-weight:700; flex-shrink:0; }
  .s-name { font-size:16px; font-weight:700; color:#0f172a; }
  .s-meta { font-size:12px; color:#64748b; margin-top:2px; }
  .s-details { display:flex; gap:24px; margin-left:auto; }
  .lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; }
  .val { font-size:12px; font-weight:600; color:#1e293b; margin-top:1px; }
  .score-section { display:flex; border-bottom:1px solid #e2e8f0; }
  .score-box { flex:1; padding:12px 16px; text-align:center; border-right:1px solid #e2e8f0; }
  .score-box:last-child { border-right:none; }
  .s-lbl { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:4px; }
  .s-val { font-size:22px; font-weight:900; }
  .s-sub { font-size:11px; color:#94a3b8; margin-top:2px; }
  .grade-circle { display:inline-flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; font-size:18px; font-weight:900; color:#fff; }
  .table-wrap { padding:14px 20px 18px; }
  .section-title { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:1px; margin-bottom:10px; }
  table { width:100%; border-collapse:collapse; }
  thead tr { background:#f1f5f9; }
  thead th { padding:8px 10px; font-size:11px; font-weight:700; color:#475569; text-align:left; border-bottom:2px solid #e2e8f0; }
  thead th.center { text-align:center; }
  tbody tr { border-bottom:1px solid #f1f5f9; }
  td { padding:8px 10px; font-size:12px; color:#334155; }
  td.center { text-align:center; }
  td.grade { font-size:14px; font-weight:800; }
  .bar-wrap { width:60px; height:6px; background:#e2e8f0; border-radius:99px; margin:0 auto; overflow:hidden; }
  .bar { height:100%; border-radius:99px; }
  tfoot tr { background:#f8fafc; border-top:2px solid #e2e8f0; }
  tfoot td { padding:10px; font-weight:700; font-size:13px; color:#0f172a; }
  .card-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 20px; background:#f8fafc; border-top:1px solid #e2e8f0; }
  .sig-line { width:120px; border-top:1px solid #94a3b8; padding-top:4px; font-size:10px; color:#94a3b8; text-align:center; }
  .print-bar { text-align:center; padding:16px; background:#e5e7eb; position:sticky; top:0; z-index:99; }
  @media print {
    .print-bar { display:none; }
    body { background:#fff; }
    .page { box-shadow:none; margin:0; border-radius:0; }
    * { -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; }
    img { display:block!important; }
  }
</style></head><body>
<div class="print-bar">
  <button onclick="window.print()" style="background:linear-gradient(135deg,#6b21a8,#9333ea);color:#fff;border:none;padding:12px 36px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Roboto',sans-serif;box-shadow:0 4px 12px rgba(107,33,168,0.3)">
    &#128424;&nbsp; Print All ${studentResults.length} Report Cards
  </button>
  <span style="margin-left:16px;font-size:13px;color:#555">${exam?.name} — ${classes.find(c => c.id === selectedClass) ? `${classes.find(c => c.id === selectedClass).name}-${classes.find(c => c.id === selectedClass).section}` : 'All Classes'} — ${studentResults.length} students</span>
</div>
${allPages}
</body></html>`); win.document.close();
  };

  // Print class result sheet
  const printClassSheet = async () => {
    const school = await getSchoolInfo();
    const cls = classes.find(c => c.id === selectedClass);
    const headerCols = examSubjects.map(s => `<th class="center">${s.name}</th>`).join('');
    const rows = studentResults.map((sr) => {
      const subCols = examSubjects.map(sub => {
        const m = sr.subjectMap[sub.id];
        return m ? `<td class="center">${m.marksObtained}/${m.maxMarks}</td>` : `<td class="center">—</td>`;
      }).join('');
      return `<tr>
        <td class="center">${sr.rank}</td>
        <td>${sr.firstName} ${sr.lastName}</td>
        <td class="center">${sr.rollNumber || '—'}</td>
        ${subCols}
        <td class="center"><strong>${sr.total}/${sr.maxTotal}</strong></td>
        <td class="center">${sr.pct}%</td>
        <td class="center" style="color:${gradeColor(sr.grade?.grade)};font-weight:700">${sr.grade?.grade || '—'}</td>
        <td class="center" style="color:${sr.passed ? '#16a34a' : '#dc2626'};font-weight:700">${sr.passed ? 'P' : 'F'}</td>
      </tr>`;
    }).join('');

    const win = window.open('', '_blank', 'width=1100,height=700');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Class Result Sheet</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; padding:24px 28px; color:#111; font-size:12px; }
  .school-header { margin-bottom:16px; }
  .meta { display:flex; justify-content:space-between; margin-bottom:12px; font-size:12px; }
  .meta .title { font-size:15px; font-weight:700; color:#6b21a8; }
  table { width:100%; border-collapse:collapse; }
  thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:11px; padding:7px 8px; text-align:left; border-bottom:2px solid #d1d5db; white-space:nowrap; }
  thead th.center { text-align:center; }
  td { padding:6px 8px; border-bottom:1px solid #f3f4f6; vertical-align:middle; }
  td.center { text-align:center; }
  tr:nth-child(even) { background:#fafafa; }
  .footer { text-align:center; font-size:11px; color:#aaa; margin-top:20px; padding-top:8px; border-top:1px dashed #e5e7eb; }
  .print-btn { text-align:center; margin-top:14px; }
  @page { margin: 3mm; } @media print { .print-btn{display:none;} body{padding:4px;} }
</style></head><body>
<div class="school-header">
  ${schoolHeader(school, '#6b21a8', `Class Result Sheet &mdash; ${exam?.name || 'Examination'}`)}
</div>
<div class="meta">
  <div><div class="title">Class: ${cls ? `${cls.name}-${cls.section}` : 'All Classes'}</div><div style="color:#555;margin-top:2px">${studentResults.length} students</div></div>
  <div style="text-align:right;color:#555">Date: <strong>${formatDate(new Date().toISOString().split('T')[0])}</strong></div>
</div>
<table>
  <thead><tr><th class="center">Rank</th><th>Student</th><th class="center">Roll</th>${headerCols}<th class="center">Total</th><th class="center">%</th><th class="center">Grade</th><th class="center">Result</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">Computer-generated &mdash; ${school.name}</div>
<div class="print-btn"><button onclick="window.print()" style="background:#6b21a8;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:13px;cursor:pointer;font-family:'Roboto',sans-serif">&#128424; Print</button></div>
</body></html>`); win.document.close();
  };

  const selSubjectObj = subjects.find(s => s.id === selectedSubject);

  // Fee-pending gate for parent / student roles
  if (isParentRole || isStudentRole) {
    const checkStudents = isStudentRole
      ? students.filter(s => s.id === currentUser?.profileId)
      : students.filter(s => (s.parentId === currentUser?.profileId || s.id === currentUser?.profileId));

    const hasPendingFee = checkStudents.some(student => {
      const fs = feeStructures.find(f => f.classId === student.classId);
      if (fs) {
        const tuitionPaid = feePayments.filter(p => p.studentId === student.id && p.type !== 'transportFee').reduce((s, p) => s + Number(p.amount || 0), 0);
        const tuitionDue  = Number(fs.totalAmount || 0) + Number(student.previousYearBalance || 0);
        if (tuitionPaid < tuitionDue) return true;
      }
      if (student.transportRouteId) {
        const route = routes.find(r => r.id === student.transportRouteId);
        if (route) {
          const allStops = route.stops || [];
          const stop = allStops.find(s => s.name === student.transportStop) || allStops.find(s => Number(s.fee) > 0);
          const annualFee = stop ? Number(stop.fee) : Number(route.fee || 0);
          if (annualFee > 0) {
            const transPaid = feePayments.filter(p => p.studentId === student.id && p.type === 'transportFee').reduce((s, p) => s + Number(p.amount || 0), 0);
            if (transPaid < annualFee) return true;
          }
        }
      }
      return false;
    });

    if (hasPendingFee) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-10 text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
            </div>
            <h2 className="text-lg font-bold text-red-700 mb-2">Fee Payment Pending</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Please contact the <strong>Principal</strong> for the results of your {isParentRole ? 'child' : 'account'}.
            </p>
          </div>
        </div>
      );
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Results</h1>
        <div className="flex items-center gap-2">
          {!isRestricted && (
            <>
              <button onClick={printAllReportCards} disabled={studentResults.length === 0}
                className="flex items-center gap-1.5 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <Printer size={14}/> Print All Report Cards
              </button>
              <button onClick={printClassSheet} disabled={studentResults.length === 0}
                className="flex items-center gap-1.5 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <Printer size={14}/> Print Class Sheet
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div>
          <label className="block font-medium mb-1">Exam</label>
          <select value={selectedExam} onChange={e => setSelectedExam(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        {!isRestricted && (
          <div>
            <label className="block font-medium mb-1">Class</label>
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setExpandedStudent(null); }}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
              <option value="">All Classes</option>
              {availableClasses.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block font-medium mb-1">Subject</label>
          <select value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setView(e.target.value ? 'subject' : 'class'); }}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
            <option value="">All Subjects</option>
            {examSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {/* View toggle */}
        <div className="flex border border-gray-300 rounded-lg overflow-hidden ml-2">
          <button onClick={() => { setView('class'); setSelectedSubject(''); }}
            className={`px-3 py-1 text-sm ${view === 'class' ? 'bg-purple-600 text-white' : 'hover:bg-gray-50'}`}>
            Class View
          </button>
          <button onClick={() => setView('subject')}
            className={`px-3 py-1 text-sm border-l border-gray-300 ${view === 'subject' ? 'bg-purple-600 text-white' : 'hover:bg-gray-50'}`}>
            Subject View
          </button>
        </div>
      </div>

      {/* -- CLASS VIEW -- */}
      {view === 'class' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          {studentResults.length === 0 ? (
            <div className="py-12 text-center">Select a class and exam to view results.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2 text-center font-semibold w-12">Rank</th>
                  <th className="px-4 py-2 text-left font-semibold">Student</th>
                  <th className="px-3 py-2 text-center font-semibold">Roll</th>
                  {examSubjects.map(s => (
                    <th key={s.id} className="px-3 py-2 text-center font-semibold whitespace-nowrap">{s.name}</th>
                  ))}
                  <th className="px-3 py-2 text-center font-semibold">Total</th>
                  <th className="px-3 py-2 text-center font-semibold">%</th>
                  <th className="px-3 py-2 text-center font-semibold">Grade</th>
                  <th className="px-3 py-2 text-center font-semibold">Result</th>
                  {!isParentRole && <th className="px-3 py-2 text-center font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {studentResults.map(sr => (
                  <React.Fragment key={sr.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedStudent(expandedStudent === sr.id ? null : sr.id)}>
                      <td className="px-3 py-1 text-center font-semibold" style={{ color: sr.rank === 1 ? '#d97706' : sr.rank === 2 ? '#6b7280' : sr.rank === 3 ? '#92400e' : '#333' }}>
                        {sr.rank}
                      </td>
                      <td className="px-4 py-1 font-medium">
                        <div className="flex items-center gap-1">
                          {expandedStudent === sr.id ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                          {sr.firstName} {sr.lastName}
                        </div>
                      </td>
                      <td className="px-3 py-1 text-center">{sr.rollNumber || '—'}</td>
                      {examSubjects.map(sub => {
                        const m = sr.subjectMap[sub.id];
                        return (
                          <td key={sub.id} className="px-3 py-1 text-center">
                            {m ? <span style={{ color: (m.marksObtained / m.maxMarks) < 0.33 ? '#dc2626' : '#333' }}>{m.marksObtained}/{m.maxMarks}</span> : '—'}
                          </td>
                        );
                      })}
                      <td className="px-3 py-1 text-center font-semibold">{sr.total}/{sr.maxTotal}</td>
                      <td className="px-3 py-1 text-center" style={{ color: barColor(sr.pct) }}>{sr.pct}%</td>
                      <td className="px-3 py-1 text-center font-bold" style={{ color: gradeColor(sr.grade?.grade) }}>{sr.grade?.grade || '—'}</td>
                      <td className="px-3 py-1 text-center font-bold" style={{ color: sr.passed ? '#16a34a' : '#dc2626' }}>{sr.passed ? 'PASS' : 'FAIL'}</td>
                      {!isParentRole && (
                        <td className="px-3 py-1 text-center" onClick={e => e.stopPropagation()}>
                          <button onClick={() => printReportCard(sr)} className="p-1 hover:bg-purple-50 rounded text-purple-600" title="Print Report Card">
                            <Printer size={13}/>
                          </button>
                        </td>
                      )}
                    </tr>
                    {/* Expanded subject detail */}
                    {expandedStudent === sr.id && (
                      <tr>
                        <td colSpan={6 + examSubjects.length} className="px-6 py-3 bg-purple-50 border-b border-purple-100">
                          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(examSubjects.length, 4)}, 1fr)` }}>
                            {examSubjects.map(sub => {
                              const m = sr.subjectMap[sub.id];
                              if (!m) return null;
                              const pct = Math.round((m.marksObtained / m.maxMarks) * 100);
                              const grade = calculateGrade(m.marksObtained, m.maxMarks);
                              return (
                                <div key={sub.id} className="bg-white rounded-lg px-3 py-2 border border-purple-100">
                                  <div className="font-semibold mb-1">{sub.name}</div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span>{m.marksObtained}/{m.maxMarks}</span>
                                    <span className="font-bold" style={{ color: gradeColor(grade.grade) }}>{grade.grade}</span>
                                  </div>
                                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                                    <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: barColor(pct) }}/>
                                  </div>
                                  <div style={{ color: barColor(pct) }} className="mt-0.5">{pct}%</div>
                                  {m.remarks && <div className="mt-0.5 italic">{m.remarks}</div>}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* -- SUBJECT VIEW -- */}
      {view === 'subject' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          {!selectedSubject ? (
            <div className="py-12 text-center">Select a subject to view subject-wise analysis.</div>
          ) : subjectResults.length === 0 ? (
            <div className="py-12 text-center">No marks found for this subject.</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div>
                  <span className="font-semibold">{selSubjectObj?.name}</span>
                  <span className="ml-2">— Subject Analysis</span>
                </div>
                <div className="flex gap-4">
                  <span>Avg: <strong style={{ color: barColor(Math.round(subjectResults.reduce((s, r) => s + r.pct, 0) / subjectResults.length)) }}>
                    {Math.round(subjectResults.reduce((s, r) => s + r.pct, 0) / subjectResults.length)}%
                  </strong></span>
                  <span>Highest: <strong style={{ color: '#16a34a' }}>{subjectResults[0]?.marksObtained}/{subjectResults[0]?.maxMarks}</strong></span>
                  <span>Pass: <strong style={{ color: '#16a34a' }}>{subjectResults.filter(r => r.pct >= 33).length}/{subjectResults.length}</strong></span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-3 py-2 text-center font-semibold w-12">Rank</th>
                    <th className="px-4 py-2 text-left font-semibold">Student</th>
                    <th className="px-3 py-2 text-center font-semibold">Class</th>
                    <th className="px-3 py-2 text-center font-semibold">Obtained</th>
                    <th className="px-3 py-2 text-center font-semibold">Max</th>
                    <th className="px-3 py-2 text-center font-semibold">%</th>
                    <th className="px-3 py-2 text-center font-semibold">Progress</th>
                    <th className="px-3 py-2 text-center font-semibold">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectResults.map(sr => {
                    const c = classes.find(c => c.id === sr.classId);
                    return (
                      <tr key={sr.id} className="hover:bg-gray-50">
                        <td className="px-3 py-1 text-center font-semibold" style={{ color: sr.rank === 1 ? '#d97706' : '#333' }}>{sr.rank}</td>
                        <td className="px-4 py-1 font-medium">{sr.firstName} {sr.lastName}</td>
                        <td className="px-3 py-1 text-center">{c ? `${c.name}-${c.section}` : '—'}</td>
                        <td className="px-3 py-1 text-center font-semibold">{sr.marksObtained}</td>
                        <td className="px-3 py-1 text-center">{sr.maxMarks}</td>
                        <td className="px-3 py-1 text-center" style={{ color: barColor(sr.pct) }}>{sr.pct}%</td>
                        <td className="px-3 py-1">
                          <div className="w-24 bg-gray-100 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full" style={{ width: `${sr.pct}%`, background: barColor(sr.pct) }}/>
                          </div>
                        </td>
                        <td className="px-3 py-1 text-center font-bold" style={{ color: gradeColor(sr.grade?.grade) }}>{sr.grade?.grade || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
export default Results;
